import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.jsx';
import { sectionsForUser, getDefaultSection } from './helpData.js';
import HelpNav from './HelpNav.jsx';
import HelpContent from './HelpContent.jsx';
import './help.scss';

/**
 * RBAC-aware help drawer. Slides in from the right with role-scoped sections,
 * tabbed navigation, topic pills, and scrollable HTML content. The set of
 * sections is derived from the signed-in user's role (helpData.sectionsForUser),
 * and the opening section defaults to whatever page the user is currently on.
 */
export default function HelpDrawer({ isOpen, onClose }) {
  const { user } = useAuth();
  const location = useLocation();

  const sections = useMemo(() => sectionsForUser(user), [user]);
  const [activeSection, setActiveSection] = useState(() => getDefaultSection(location.pathname, user));
  const [activeTopic, setActiveTopic] = useState(null);

  // When opened (or the route changes while closed), jump to the most relevant
  // section for the current page and its first topic.
  useEffect(() => {
    if (!isOpen) return;
    const sectionId = getDefaultSection(location.pathname, user);
    setActiveSection(sectionId);
    const section = sections.find((s) => s.id === sectionId);
    setActiveTopic(section?.topics[0]?.id ?? null);
  }, [isOpen, location.pathname, user, sections]);

  // Escape to close + lock body scroll while open.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const section = sections.find((s) => s.id === activeSection) || sections[0];

  function changeSection(id) {
    setActiveSection(id);
    const s = sections.find((x) => x.id === id);
    setActiveTopic(s?.topics[0]?.id ?? null);
  }

  return (
    <div className="help-overlay" role="presentation">
      <div className="help-backdrop" onClick={onClose} aria-hidden="true" />
      <aside className="help-drawer" role="dialog" aria-modal="true" aria-label="Help and Documentation">
        <header className="help-header">
          <div>
            <h2 className="h6 mb-0 fw-bold">Help &amp; Documentation</h2>
            <small className="help-header-sub">{user?.role ? `Tailored for: ${user.role}` : 'TPA InsureOS'}</small>
          </div>
          <button type="button" className="help-close" onClick={onClose} aria-label="Close help">
            <i className="fa fa-xmark" aria-hidden="true" />
          </button>
        </header>
        <HelpNav sections={sections} activeSection={activeSection} onSectionChange={changeSection} />
        {section && (
          <HelpContent
            section={section}
            activeTopic={activeTopic ?? section.topics[0]?.id}
            onTopicChange={setActiveTopic}
          />
        )}
      </aside>
    </div>
  );
}
